import { Injectable } from '@angular/core';
import { ConsentStatus } from '../../interfaces/consent-status';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ConsentRequestType } from '../../interfaces/consent-request-type';
import { Apollo, QueryRef } from 'apollo-angular';
import {
  ConsentRequestAssignees,
  ConsentRequestFilterInput,
  CREATE_CONSENT_REQUEST,
  CreateConsentRequestInput,
  DELETE_CONSENT_REQUEST,
  FETCH_CONSENT_REQUEST_PAGE,
  FETCH_CONSENT_REQUESTS,
  FetchConsentRequestPageResponse,
  FetchConsentRequestsPageVariables,
  UPDATE_CONSENT_REQUEST,
  UpdateConsentRequestInput,
} from './graphql/consent-requests';
import Permission from '../../interfaces/permission';
import { ConsentRequestRoleGroup } from '../../interfaces/consent-request-role-group';
import { Paginator, PaginatorInfo } from '../../types/paginator';
import { ConsentRequest } from '../../interfaces/consent-request';
import { Person } from '../../interfaces/person';
import KeyMirror from '../../types/key-mirror';
import ID from '../../types/id';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';

const config = new MatSnackBarConfig();
config.panelClass = ['save-success'];
config.horizontalPosition = 'center';
config.verticalPosition = 'top';
config.duration = 3000;
@Injectable({
  providedIn: 'root',
})
export class ConsentRequestsService {
  private _consentStatuses: ConsentStatus[] = [];
  private consentStatuses = new BehaviorSubject(this._consentStatuses);
  public consentStatuses$ = this.consentStatuses.asObservable();

  private _consentRequestTypes: ConsentRequestType[] = [];
  private consentRequestTypes = new BehaviorSubject(this._consentRequestTypes);
  public consentRequestTypes$ = this.consentRequestTypes.asObservable();

  private _loading = false;
  private loading = new BehaviorSubject(this._loading);
  public loading$ = this.loading.asObservable();

  private _consentsLoading: boolean = false;
  private consentsLoading = new BehaviorSubject(this._consentsLoading);
  public consentsLoading$ = this.consentsLoading.asObservable();

  private _permission: Permission | null = null;
  private permission = new BehaviorSubject(this._permission);
  public permission$ = this.permission.asObservable();

  private _roleGroupColumns: ConsentRequestRoleGroup[] = [];
  private roleGroupColumns = new BehaviorSubject(this._roleGroupColumns);
  public roleGroupColumns$ = this.roleGroupColumns.asObservable();

  private _consentRequestAssignees: ConsentRequestAssignees = {
    assetManagers: [],
    fundManagers: [],
    underwriters: [],
    consentManagers: [],
    investorRelations: [],
    dispositionAssociates: [],
  };
  private consentRequestAssignees: KeyMirror<
    ConsentRequestAssignees,
    BehaviorSubject<Person[]>
  > = {
    assetManagers: new BehaviorSubject(
      this._consentRequestAssignees.assetManagers
    ),
    fundManagers: new BehaviorSubject(
      this._consentRequestAssignees.fundManagers
    ),
    underwriters: new BehaviorSubject(
      this._consentRequestAssignees.underwriters
    ),
    consentManagers: new BehaviorSubject(
      this._consentRequestAssignees.consentManagers
    ),
    investorRelations: new BehaviorSubject(
      this._consentRequestAssignees.investorRelations
    ),
    dispositionAssociates: new BehaviorSubject(
      this._consentRequestAssignees.dispositionAssociates
    ),
  };
  public consentRequestAssignees$: KeyMirror<
    ConsentRequestAssignees,
    Observable<Person[]>
  > = {
    assetManagers: this.consentRequestAssignees.assetManagers.asObservable(),
    fundManagers: this.consentRequestAssignees.fundManagers.asObservable(),
    underwriters: this.consentRequestAssignees.underwriters.asObservable(),
    consentManagers:
      this.consentRequestAssignees.consentManagers.asObservable(),
    investorRelations:
      this.consentRequestAssignees.investorRelations.asObservable(),
    dispositionAssociates:
      this.consentRequestAssignees.dispositionAssociates.asObservable(),
  };

  private _consentRequests: Paginator<ConsentRequest, PaginatorInfo> = {
    data: [],
    paginatorInfo: { currentPage: 1 },
  };
  private consentRequests = new BehaviorSubject(this._consentRequests);
  public consentRequests$ = this.consentRequests.asObservable();

  getPageSub: Subscription;

  private pageQuery: QueryRef<
    FetchConsentRequestPageResponse,
    FetchConsentRequestsPageVariables
  >;

  constructor(public apollo: Apollo, private _snackBar: MatSnackBar) {}

  private setRoleGroupColumns(roleGroupColumns: ConsentRequestRoleGroup[]) {
    this._roleGroupColumns = roleGroupColumns;
    this.roleGroupColumns.next(this._roleGroupColumns);
  }
  private setConsentRequestTypes(consentRequestTypes: ConsentRequestType[]) {
    this._consentRequestTypes = consentRequestTypes;
    this.consentRequestTypes.next(this._consentRequestTypes);
  }
  private setConsentStatuses(consentStatuses: ConsentStatus[]) {
    this._consentStatuses = consentStatuses;
    this.consentStatuses.next(this._consentStatuses);
  }
  private setPermissions(permissions: Permission) {
    this._permission = permissions;
    this.permission.next(this._permission);
  }
  private setConsentRequestAssignees(assignees: ConsentRequestAssignees) {
    let keys = Object.keys(
      this._consentRequestAssignees
    ) as unknown[] as (keyof ConsentRequestAssignees)[];
    for (let key of keys) {
      this._consentRequestAssignees[key] = assignees[key];
      this.consentRequestAssignees[key].next(
        this._consentRequestAssignees[key]
      );
    }
  }
  private setConsentRequests(
    consentRequests: Paginator<ConsentRequest, PaginatorInfo>
  ) {
    this._consentRequests = consentRequests;
    this.consentRequests.next(this._consentRequests);
  }
  private setLoading(loading: boolean) {
    this.setConsentsLoading(loading);
    this._loading = loading;
    this.loading.next(this._loading);
  }
  private setConsentsLoading(loading: boolean) {
    this._consentsLoading = loading;
    this.consentsLoading.next(this._consentsLoading);
  }
  public getPage(
    page: number,
    filter?: ConsentRequestFilterInput | null,
    showComplete?: boolean | null
  ) {
    this.setConsentsLoading(true);
    let excludeStatuses: string[] | null = ['Complete', 'Denied', 'GP Withdrew'];
    if (showComplete === true) excludeStatuses = null;
    // if Deal id is set, or if status is set, allow complete requests to be shown
    if (filter?.consent_status_id || filter?.project_id) {
      excludeStatuses = null;
    }
    this.pageQuery = this.apollo.watchQuery<
      FetchConsentRequestPageResponse,
      FetchConsentRequestsPageVariables
    >({
      query: FETCH_CONSENT_REQUEST_PAGE,
      variables: { page, excludeStatuses, filter },
      fetchPolicy: 'network-only',
    });

    this.getPageSub = this.pageQuery.valueChanges.subscribe(({ data }) => {
      this.setConsentsLoading(false);
      if (data) {
        this.setConsentRequests(
          data.getConsentRequests as Paginator<ConsentRequest, PaginatorInfo>
        );
        this.setConsentRequestAssignees(
          data.getConsentRequestAssignees as ConsentRequestAssignees
        );
      }
    });
  }
  public fetchConsentRequests(showComplete: Boolean = false) {
    this.setLoading(true);
    let sub = this.apollo
      .query({
        query: FETCH_CONSENT_REQUESTS,
        variables: {
          page: 1,
          excludeStatuses: showComplete ? null : ['Complete', 'Denied', 'GP Withdrew'],
        },
      })
      .subscribe(({ data }) => {
        this.setLoading(false);
        if (!data) return;
        this.setConsentStatuses(data.getConsentStatuses);
        this.setConsentRequestTypes(data.getConsentRequestTypes);
        this.setRoleGroupColumns(data.getConsentRequestRoleGroups);
        this.setConsentRequestAssignees(data.getConsentRequestAssignees);
        if (data.me.permissionsForResource)
          this.setPermissions(data.me.permissionsForResource);
        this.setConsentRequests(data.getConsentRequests);
        sub?.unsubscribe();
      });
  }
  public tearDown() {
    this.getPageSub?.unsubscribe();
  }
  public getRequestStatus(id: string | null) {
    return (
      this._consentStatuses.find((requestStatus) => requestStatus.id === id) ??
      null
    );
  }
  public getRequestType(id: string | null) {
    return (
      this._consentRequestTypes.find((requestType) => requestType.id === id) ??
      null
    );
  }
  public createConsentRequest(input: CreateConsentRequestInput) {
    this.setConsentsLoading(true);
    return this.apollo.mutate({
      mutation: CREATE_CONSENT_REQUEST,
      variables: { input },
    });
  }
  public updateConsentRequest(id: ID, input: UpdateConsentRequestInput) {
    this.setConsentsLoading(true);
    return this.apollo.mutate({
      mutation: UPDATE_CONSENT_REQUEST,
      variables: { id, input },
    });
  }
  public deleteConsentRequest(id: ID) {
    this.setConsentsLoading(true);
    return this.apollo.mutate({
      mutation: DELETE_CONSENT_REQUEST,
      variables: { id },
    });
  }
}
